/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Application } from "@hotwired/stimulus";
import { Search, Package, ShoppingCart, User, TrendingUp, Settings as SettingsIcon, FileText, LogIn, LogOut, HelpCircle, Eye, ChevronDown, SearchIcon } from 'lucide-react';
import { useHover, useFilter } from 'react-aria';
import { DialogTrigger, Button as AriaButton, TooltipTrigger } from 'react-aria-components';
import CheckboxSelectAllController from '../controllers/checkbox_select_all_controller';
import PopoverController from '../controllers/popover_controller';
import Tooltip from './ui/tooltips/Tooltip';
import { Popover } from './ui/Popover';
import { AriaTooltipStyled } from './ui/AriaTooltipStyled';
import ConveyorShell from './search/ConveyorShell';
import Basket from './search/Basket';
import ImpactSummary from './search/ImpactSummary';
import useSupabase from '../hooks/useSupabase';
import CartModal from './modals/CartModal';
import ManifestoModal from './modals/ManifestoModal';
import PropDetailsModal from './modals/PropDetailsModal';
import LanguageToggle from './ui/LanguageToggle';
import { useLanguage } from '../i18n/LanguageContext';
import { supabase } from '../supabaseClient';
import { useToast } from './ui/ToastContext';
import { GuframPropBrowser, ViewSwitch } from './gufram';
import '../components/gufram/gufram.css';
import { SidebarProvider } from './ui/sidebar';
import OnLoan from './pages/OnLoan';
import Feedback from './pages/Feedback';
import Profile from './pages/Profile';
import SeeYourImpact from './pages/SeeYourImpact';
import SettingsPage from './pages/Settings';


// DaisyUI-backed helpers (React Aria owns behavior; DaisyUI/Tailwind owns appearance)
const LocalStacksIcon = ({ className = "" }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <g id="SVGRepo_bgCarrier" strokeWidth="0"></g>
    <g id="SVGRepo_tracerCarrier" strokeLinecap="round" strokeLinejoin="round"></g>
    <g id="SVGRepo_iconCarrier">
      <path
        d="M7 14.0014H17M7 14.0014V11.6014C7 11.0413 7 10.7613 7.10899 10.5474C7.20487 10.3592 7.35785 10.2062 7.54601 10.1104C7.75992 10.0014 8.03995 10.0014 8.6 10.0014H15.4C15.9601 10.0014 16.2401 10.0014 16.454 10.1104C16.6422 10.2062 16.7951 10.3592 16.891 10.5474C17 10.7613 17 11.0413 17 11.6014V14.0014M7 14.0014V18.0014V21.0014M17 14.0014V18.0014V21.0014M18.3466 6.17468L14.1466 4.07468C13.3595 3.68113 12.966 3.48436 12.5532 3.40691C12.1876 3.33832 11.8124 3.33832 11.4468 3.40691C11.034 3.48436 10.6405 3.68113 9.85338 4.07468L5.65337 6.17468C4.69019 6.65627 4.2086 6.89707 3.85675 7.25631C3.5456 7.574 3.30896 7.95688 3.16396 8.37725C3 8.85262 3 9.39106 3 10.4679V19.4014C3 19.9614 3 20.2414 3.10899 20.4554C3.20487 20.6435 3.35785 20.7965 3.54601 20.8924C3.75992 21.0014 4.03995 21.0014 4.6 21.0014H19.4C19.9601 21.0014 20.2401 21.0014 20.454 20.8924C20.6422 20.7965 20.7951 20.6435 20.891 20.4554C21 20.2414 21 19.9614 21 19.4014V10.4679C21 9.39106 21 8.85262 20.836 8.37725C20.691 7.95688 20.4544 7.574 20.1433 7.25631C19.7914 6.89707 19.3098 6.65627 18.3466 6.17468Z"
        stroke="#000000"
        strokeWidth="1.44"
        strokeLinecap="round"
        strokeLinejoin="round"
      ></path>
    </g>
  </svg>
);

function CircleIconButton({ className = "", children, ...props }) {
  return (
    <AriaButton
      {...props}
      className={[
        // DaisyUI shape + sizing
        "btn btn-circle rounded-full w-10 h-10 min-w-10 min-h-10 p-0 flex items-center justify-center aspect-square",
        // Keep our existing visual language
        "bg-white border border-gray-300 text-gray-500 shadow-sm",
        "hover:bg-gray-100 hover:shadow-md hover:text-gray-700 hover:border-gray-400",
        // Remove focus ring
        "focus:outline-none focus-visible:ring-0",
        // Ensure above the divider line
        "relative z-20",
        className,
      ].join(" ")}
    >
      {children}
    </AriaButton>
  );
}
function MenuTileButton({ className = "", icon: Icon, label, ...props }) {
  return (
    <button
      {...props}
      className={[
        "btn btn-ghost",
        "h-auto min-h-0 py-4 px-3",
        "flex flex-col items-center justify-center gap-1",
        "bg-gray-100 hover:bg-blue-100 hover:shadow-lg hover:shadow-blue-300/50",
        "rounded-lg transition-all",
        "focus:outline-none focus-visible:ring-0",
        className,
      ].join(" ")}
    >
      {Icon ? <Icon className="w-6 h-6 text-gray-700" /> : null}
      <span className="text-xs text-center">{label}</span>
    </button>
  );
}

function MenuRowButton({ className = "", icon: Icon, label, ...props }) {
  return (
    <button
      {...props}
      className={[
        "w-full",
        "flex items-center justify-start gap-2",
        "px-2.5 py-1.5",
        "rounded-lg",
        "bg-transparent hover:bg-blue-100 active:bg-blue-100",
        "transition-all",
        "focus:outline-none focus-visible:ring-0",
        "hover:shadow-sm hover:shadow-blue-300/40",
        "text-left",
        className,
      ].join(" ")}
    >
      {Icon ? <Icon className="w-4 h-4 text-gray-700 flex-shrink-0" /> : null}
      <span className="text-xs text-gray-700 flex-1 text-left">{label}</span>
    </button>
  );
}

const DEMO_EMAIL_PASSWORD = 'password123';

const DEFAULT_ORGANIZATION_NAME = 'Propshare Network';

const createGuestProfile = () => ({
  id: '',
  email: '',
  name: 'Propshare Guest',
  role: 'Guest',
  organization: DEFAULT_ORGANIZATION_NAME,
  avatar_url: '',
  metadata: {}
});

const WAREHOUSE_MANAGER_EMAILS = (process.env.REACT_APP_WAREHOUSE_MANAGER_EMAILS || '')
  .split(',')
  .map(email => email.trim().toLowerCase())
  .filter(Boolean);

const buildUserProfileFromSupabase = (user) => {
  if (!user) {
    return createGuestProfile();
  }

  const metadata = user.user_metadata || {};
  const email = user.email || '';
  const fallbackName = email ? email.split('@')[0] : 'Propshare Guest';

  return {
    id: user.id,
    email,
    name: metadata.full_name || metadata.name || fallbackName,
    role: metadata.role || 'Guest',
    organization: metadata.organization || DEFAULT_ORGANIZATION_NAME,
    avatar_url: metadata.avatar_url || '',
    metadata
  };
};

function SearchV2() {
  // ============================================================================
  // Hooks
  // ============================================================================
  const { fetchOrganizations, fetchInitialData, fetchPropsForGufram, submitReservationRequest, submitFeedback, fetchCategories } = useSupabase();
  const { t } = useLanguage();
  const { toast } = useToast();
  const [emailOnlyAuthEmail, setEmailOnlyAuthEmail] = useState('');
  const [authLoading, setAuthLoading] = useState(false);
  const [authError, setAuthError] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [sessionChecked, setSessionChecked] = useState(false);
  const [userProfile, setUserProfile] = useState(buildUserProfileFromSupabase(null));

  const updateAuthStateFromSession = useCallback((session) => {
    const user = session?.user ?? null;
    setUserProfile(buildUserProfileFromSupabase(user));
    setIsAuthenticated(!!session);
  }, []);

  // Prevent the rest of the page from scrolling while SearchV2 is mounted.
  useEffect(() => {
    if (typeof document === 'undefined') {
      return;
    }

    const docEl = document.documentElement;
    const bodyEl = document.body;
    const originalDocOverflow = docEl.style.overflow;
    const originalBodyOverflow = bodyEl.style.overflow;

    docEl.style.overflow = 'hidden';
    bodyEl.style.overflow = 'hidden';

    return () => {
      docEl.style.overflow = originalDocOverflow;
      bodyEl.style.overflow = originalBodyOverflow;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    const checkSession = async () => {
      try {
        const { data } = await supabase.auth.getSession();
        if (isMounted) {
          updateAuthStateFromSession(data?.session);
        }
      } catch (error) {
        console.error('Unable to verify Supabase session', error);
      } finally {
        if (isMounted) {
          setSessionChecked(true);
        }
      }
    };

    checkSession();

    const { data: authListener } = supabase.auth.onAuthStateChange((_event, session) => {
      if (isMounted) {
        updateAuthStateFromSession(session);
      }
    });

    return () => {
      isMounted = false;
      authListener.subscription?.unsubscribe();
    };
  }, [updateAuthStateFromSession]);

  const handleEmailOnlyAuthSubmit = async (event) => {
    event.preventDefault();
    const trimmedEmail = emailOnlyAuthEmail.trim().toLowerCase();

    if (!trimmedEmail) {
      setAuthError('Please enter the email address you want to use.');
      return;
    }

    setAuthLoading(true);
    setAuthError('');

    try {
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: trimmedEmail,
        password: DEMO_EMAIL_PASSWORD,
      });

      if (!signInError) {
        const { data } = await supabase.auth.getSession();
        updateAuthStateFromSession(data?.session);
        return;
      }

      const { error: signUpError } = await supabase.auth.signUp({
        email: trimmedEmail,
        password: DEMO_EMAIL_PASSWORD,
      });

      if (signUpError) {
        throw signUpError;
      }

      const { data } = await supabase.auth.getSession();
      updateAuthStateFromSession(data?.session);
    } catch (error) {
      console.error('Email-only authentication failed', error);
      setAuthError(error?.message || 'We couldn’t create an account for that address.');
    } finally {
      setAuthLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await supabase.auth.signOut();
    } catch (error) {
      console.error('Logout failed', error);
    } finally {
      setAuthError('');
      setEmailOnlyAuthEmail('');
      updateAuthStateFromSession(null);
      setSessionChecked(true);
    }
  };

  // ============================================================================
  // UI State
  // ============================================================================
  const [currentPage, setCurrentPage] = useState('search'); // 'search', 'onloan', 'feedback', 'profile', 'impact', 'settings'
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [selectedStacks, setSelectedStacks] = useState([]);
  const [impactMode, setImpactMode] = useState(null);
  const [showAllTooltips, setShowAllTooltips] = useState(false);
  const [warehouseFilter, setWarehouseFilter] = useState('');
  const [isLogoHovered, setIsLogoHovered] = useState(false);
  const logoButtonRef = useRef(null);
  const [shouldRenderDropdownDrawer, setShouldRenderDropdownDrawer] = useState(false);
  const [isDropdownAnimating, setIsDropdownAnimating] = useState(false);
  const warehouseLoginHref =
    typeof window === 'undefined'
      ? '/?v1&role=warehouse_admin'
      : `${window.location.origin}/?v1&role=warehouse_admin`;
  const canUseWarehouseLogin =
    WAREHOUSE_MANAGER_EMAILS.length === 0 ||
    (userProfile?.email && WAREHOUSE_MANAGER_EMAILS.includes(userProfile.email.toLowerCase()));

  // Organizations from Supabase
  const [organizations, setOrganizations] = useState([]);
  const [organizationsLoading, setOrganizationsLoading] = useState(true);
  const [categories, setCategories] = useState([]);

  // Props from Supabase
  const [props, setProps] = useState([]);
  const [propsLoading, setPropsLoading] = useState(true);

  // Gufram-formatted props from Supabase
  const [guframProps, setGuframProps] = useState([]);
  const [guframPropsLoading, setGuframPropsLoading] = useState(true);

  const isInitialLoading = organizationsLoading || propsLoading || guframPropsLoading;

  // Cart Modal State
  const [cartModalOpen, setCartModalOpen] = useState(false);
  const [cartModalOrgId, setCartModalOrgId] = useState(null);
  const [cartModalBasketPos, setCartModalBasketPos] = useState(null);

  // Manifesto Modal State
  const [manifestoModalOpen, setManifestoModalOpen] = useState(false);
  const [detailProp, setDetailProp] = useState(null);

  // ============================================================================
  // CART V2 STATE - Single Source of Truth
  // ============================================================================
  
  // Primary state: array of selected items
  const [selectedItems, setSelectedItems] = useState([]);

  // Derived state: group items by organization
  const selectedByOrg = useMemo(() => {
    return selectedItems.reduce((acc, item) => {
      if (!acc[item.orgId]) {
        acc[item.orgId] = {
          orgId: item.orgId,
          orgName: item.orgName,
          items: [],
          itemCount: 0
        };
      }
      acc[item.orgId].items.push(item);
      acc[item.orgId].itemCount++;
      return acc;
    }, {});
  }, [selectedItems]);

  // Derived: total count across all orgs
  const totalSelectedCount = useMemo(() => {
    return selectedItems.length;
  }, [selectedItems]);

  // Derived: list of org IDs with selections
  const orgsWithSelections = useMemo(() => {
    return Object.keys(selectedByOrg);
  }, [selectedByOrg, userProfile]);

  // ============================================================================
  // CART V2 ACTIONS
  // ============================================================================

  // Get item count for specific org (used by Basket component)
  const getOrgItemCount = (orgId) => {
    return selectedByOrg[orgId]?.itemCount ?? 0;
  };

  // Add item to selection (only if not already added)
  const addToSelection = (prop) => {
    // Check if item is already in cart
    const alreadyAdded = selectedItems.some(item => item.propId === prop.id);
    if (alreadyAdded) {
      console.log('Item already in cart:', prop.id);
      return;
    }
    
    const item = {
      propId: prop.id,
      orgId: prop.owner_org_id,
      orgName: prop.organization?.name || 'Unknown',
      propName: prop.name,
      imageUrl: prop.images?.[0],
      addedAt: Date.now()
    };
    setSelectedItems(prev => [...prev, item]);
  };

  // Remove item from selection
  const removeFromSelection = (propId) => {
    setSelectedItems(prev => prev.filter(item => item.propId !== propId));
  };

  // Toggle item (add if absent, remove if present)
  const toggleSelection = (prop) => {
    setSelectedItems(prev => {
      const exists = prev.some(item => item.propId === prop.id);
      if (exists) {
        return prev.filter(item => item.propId !== prop.id);
      } else {
        return [...prev, {
          propId: prop.id,
          orgId: prop.owner_org_id,
          orgName: prop.organization?.name || 'Unknown',
          propName: prop.name,
          imageUrl: prop.images?.[0],
          addedAt: Date.now()
        }];
      }
    });
  };

  // Clear all selections
  const clearSelection = () => {
    setSelectedItems([]);
  };

  // Clear selections for specific org (for post-checkout cleanup)
  const clearOrgSelection = (orgId) => {
    setSelectedItems(prev => prev.filter(item => item.orgId !== orgId));
  };

  const markPropsStatus = (propIds, updates = {}) => {
    setProps(prev =>
      prev.map(prop =>
        propIds.includes(prop.id)
          ? { ...prop, ...(updates.status ? { status: updates.status } : {}), ...(updates.reservationId ? { reservation_id: updates.reservationId } : {}) }
          : prop
      )
    );
    setGuframProps(prev =>
      prev.map(item =>
        propIds.includes(item.id)
          ? { ...item, ...(updates.status ? { status: updates.status } : {}), ...(updates.reservationId ? { reservation_id: updates.reservationId } : {}) }
          : item
      )
    );
  };

  // Check if prop is selected
  const warehouses = useMemo(() => {
    return organizations.map(org => org.name);
  }, [organizations]);

  useEffect(() => {
    if (warehouses.length > 0 && selectedStacks.length === 0) {
      setSelectedStacks(warehouses);
    }
  }, [warehouses, selectedStacks.length]);

  const usageCategoryLookup = useMemo(() => {
    const map = new Map();
    categories.forEach(category => {
      if (category?.id) {
        map.set(category.id, category.name);
      }
    });
    return map;
  }, [categories]);

  const organizationNameToId = useMemo(() => {
    const map = {};
    organizations.forEach(org => {
      map[org.name] = org.id;
    });
    return map;
  }, [organizations]);

  const isSelected = (propId) => {
    return selectedItems.some(item => item.propId === propId);
  };

  const resolvePropForDetails = useCallback((payload) => {
    if (!payload) return null;
    let target = payload;
    if (target.prop) {
      target = target.prop;
    }
    let id = null;

    if (typeof target === 'object') {
      id = target.id ?? target._original?.id;
    } else {
      id = target;
    }

    if (id == null || id === '') {
      return typeof target === 'object' ? target : null;
    }

    const normalizedId = String(id);
    const directMatch = props.find(p => String(p.id) === normalizedId);
    if (directMatch) return directMatch;

    const fallback =
      guframProps.find(item => String(item.id) === normalizedId) ||
      guframProps.find(item => String(item.slug) === normalizedId);
    if (fallback) {
      return fallback;
    }

    return typeof target === 'object' ? target : null;
  }, [props, guframProps]);

  const handleOpenPropDetails = useCallback((payload) => {
    const resolved = resolvePropForDetails(payload);
    if (resolved) {
      const usageCategoryName = usageCategoryLookup.get(resolved.usage_category_id);
      setDetailProp({
        ...resolved,
        usage_category_name: usageCategoryName || resolved.usage_category_name,
      });
    }
  }, [resolvePropForDetails, usageCategoryLookup]);

  const handleFeedbackSubmit = useCallback(async (feedbackData) => {
    if (!submitFeedback) {
      throw new Error('Feedback service unavailable');
    }
    await submitFeedback(userProfile, feedbackData);
    toast.success('Feedback submitted. Thank you!');
  }, [submitFeedback, userProfile, toast]);

  const handleAddToCartFromModal = useCallback((propId) => {
    const resolved = resolvePropForDetails(propId);
    if (resolved) {
      addToSelection(resolved);
    }
  }, [resolvePropForDetails, addToSelection]);

  const handleClosePropDetails = useCallback(() => {
    setDetailProp(null);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const isMobileViewport = window.matchMedia("(max-width: 640px)").matches;
    if (!isMobileViewport) return;

    if (detailProp) {
      document.body.classList.add('-flow-paused');
    } else {
      document.body.classList.remove('-flow-paused');
    }

    return () => {
      document.body.classList.remove('-flow-paused');
    };
  }, [detailProp]);
  
  const { hoverProps, isHovered } = useHover({});
  const showNeonLogo = isDropdownOpen || isLogoHovered;

  // ============================================================================
  // Load Data from Supabase
  // ============================================================================
  useEffect(() => {
    const loadData = async () => {
      try {
        setOrganizationsLoading(true);
        setPropsLoading(true);
        setGuframPropsLoading(true);
        
        // Load organizations and regular props
        const data = await fetchInitialData();
        
        setOrganizations(data.organizations || []);
        console.log('Loaded organizations:', data.organizations);
        
        // Transform props to include organization reference
        const propsWithOrgs = (data.props || []).map(prop => {
          const org = (data.organizations || []).find(o => o.id === prop.owner_org_id);
          return {
            ...prop,
            organization: org || null
          };
        });
        setProps(propsWithOrgs);
        console.log('Loaded props:', propsWithOrgs);
        
        // Load Gufram-formatted props
        const guframData = await fetchPropsForGufram();
        setGuframProps(guframData || []);
        console.log('Loaded Gufram props:', guframData);

        const categoryData = await fetchCategories();
        setCategories(categoryData || []);
        console.log('Loaded categories:', categoryData);

      } catch (error) {
        console.error('Error loading data:', error);
        setOrganizations([]);
        setProps([]);
        setGuframProps([]);
        setCategories([]);
      } finally {
        setOrganizationsLoading(false);
        setPropsLoading(false);
        setGuframPropsLoading(false);
      }
    };

    loadData();
  }, []); // Run once on mount

  const { contains } = useFilter({ sensitivity: 'base' });
  const filteredWarehouses = warehouses.filter(warehouse => 
    contains(warehouse, warehouseFilter)
  );

  useEffect(() => {
    const application = Application.start();
    application.register("checkbox-select-all", CheckboxSelectAllController);
    application.register("popover", PopoverController);
    return () => application.stop();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!isDropdownOpen) return;

    const handleClickOutside = (event) => {
      if (!event.target.closest('.dropdown-container')) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('pointerdown', handleClickOutside);
    return () => document.removeEventListener('pointerdown', handleClickOutside);
  }, [isDropdownOpen]);

  // Animate dropdown drawer opening/closing
  useEffect(() => {
    if (isDropdownOpen) {
      setShouldRenderDropdownDrawer(true);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setIsDropdownAnimating(true);
        });
      });
      return;
    }

    if (!shouldRenderDropdownDrawer) return;

    setIsDropdownAnimating(false);
    const timer = setTimeout(() => setShouldRenderDropdownDrawer(false), 500);
    return () => clearTimeout(timer);
  }, [isDropdownOpen, shouldRenderDropdownDrawer]);

  // Close dropdown on Escape and return focus to the logo button
  useEffect(() => {
    if (!isDropdownOpen) return;

    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        setIsDropdownOpen(false);
        // Return focus after state updates
        setTimeout(() => logoButtonRef.current?.focus?.(), 0);
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isDropdownOpen]);

  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);
  const basketScrollRef = useRef(null);

  // Check scroll position to show/hide arrows
  const checkScrollPosition = () => {
    const container = basketScrollRef.current;
    if (!container) return;

    const { scrollLeft, scrollWidth, clientWidth } = container;
    const hasOverflow = scrollWidth > clientWidth;
    const canScrollLeft = scrollLeft > 10;
    const canScrollRight = hasOverflow && scrollLeft < scrollWidth - clientWidth - 10;
    
    // Only update state if values actually changed to prevent infinite loops
    setShowLeftArrow(prev => prev !== canScrollLeft ? canScrollLeft : prev);
    setShowRightArrow(prev => prev !== canScrollRight ? canScrollRight : prev);
  };

  // Scroll basket container by a specified amount
  const scrollBaskets = (direction) => {
    if (!basketScrollRef.current) return;
    
    const scrollAmount = direction === 'left' ? -200 : 200;
    basketScrollRef.current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    
    // Check scroll position after animation completes
    setTimeout(checkScrollPosition, 300);
  };

  // Check arrows on mount and when baskets change
  useEffect(() => {
    const timer = setTimeout(checkScrollPosition, 100);
    window.addEventListener('resize', checkScrollPosition);
    
    // CRITICAL: Wake up React Aria's hover system when baskets first appear
    // React Aria needs to detect actual layout changes, not just resize events
    if (selectedStacks.length > 0 && basketScrollRef.current) {
      setTimeout(() => {
        const container = basketScrollRef.current;
        if (container) {
          // Force actual layout change by temporarily modifying width
          const originalWidth = container.style.width;
          container.style.width = 'calc(100% - 1px)';
          
          // Force reflow by reading layout property
          const _forceReflow = container.offsetHeight;
          
          // Restore and dispatch resize
          container.style.width = originalWidth;
          window.dispatchEvent(new Event('resize'));
        }
      }, 150);
    }
    
    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', checkScrollPosition);
    };
  }, [selectedStacks]);

  const toggleStack = (warehouse) => {
    if (selectedStacks.includes(warehouse)) {
      setSelectedStacks(selectedStacks.filter(s => s !== warehouse));
    } else {
      setSelectedStacks([...selectedStacks, warehouse]);
    }
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedStacks([...filteredWarehouses]);
    } else {
      setSelectedStacks([]);
    }
  };

  // Basket click handler - opens cart modal with basket position
  const handleBasketClick = useCallback((orgId, event) => {
    // Get items for this specific organization
    const orgItems = selectedByOrg[orgId];
    if (!orgItems || orgItems.itemCount === 0) {
      console.log(`Cannot open cart modal: basket is empty for org ${orgId}`);
      return;
    }

    // Get basket position for modal animation
    const basketElement = event?.currentTarget;
    if (basketElement) {
      const rect = basketElement.getBoundingClientRect();
      setCartModalBasketPos({
        x: rect.left + rect.width / 2,
        y: rect.top + rect.height / 2,
      });
    }

    // Open modal for this org
    setCartModalOrgId(orgId);
    setCartModalOpen(true);
  }, [selectedByOrg]);

  // Handle checkout from modal
  const handleModalCheckout = useCallback(async (checkoutData) => {
    const {
      orgId,
      items: confirmedItems = [],
      expectedReturnDate,
      purpose,
      notes,
      preferredPickupDay,
      preferredTimeRange
    } = checkoutData;

    if (!confirmedItems || confirmedItems.length === 0) {
      toast.error('No confirmed props selected for checkout.', { title: 'Checkout Error' });
      return;
    }

    if (!expectedReturnDate) {
      toast.error('Please select an expected return date.', { title: 'Missing Return Date' });
      return;
    }

    const propIds = confirmedItems.map(item => item.propId);
    const purposeValue = purpose?.trim() || 'General Use';

    try {
      const createdReservations = await submitReservationRequest(propIds, userProfile, expectedReturnDate, purposeValue, notes || '', {
        preferredPickupDay: preferredPickupDay || undefined,
        preferredTimeRange: preferredTimeRange || undefined
      });

      toast.success('Reservation submitted for review.', { title: 'Reservation Sent' });
      if (Array.isArray(createdReservations)) {
        createdReservations.forEach(({ reservation, propIds: reservationPropIds }) => {
          if (!reservationPropIds || reservationPropIds.length === 0) return;
          markPropsStatus(reservationPropIds, { status: 'reserved', reservationId: reservation?.id });
        });
      }
      clearOrgSelection(orgId);
      setCartModalOpen(false);
      setCartModalOrgId(null);
      setCartModalBasketPos(null);
    } catch (error) {
      console.error('Error submitting reservation:', error);
      const errorMessage = error?.message || 'Unable to submit reservation. Please try again.';
      toast.error(errorMessage, { title: 'Checkout Failed' });
    }
  }, [clearOrgSelection, markPropsStatus, submitReservationRequest, userProfile, toast]);

  // Get modal data for current org
  const cartModalData = cartModalOrgId ? selectedByOrg[cartModalOrgId] : null;

  if (!sessionChecked) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950 px-4">
        <div className="text-center space-y-2 text-white">
          <div className="mx-auto h-12 w-12 animate-spin rounded-full border-4 border-white border-t-transparent"></div>
          <p className="text-lg font-semibold">Checking Supabase session…</p>
          <p className="text-sm text-slate-300">We just need a moment to confirm your login.</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950 px-4">
        <div className="w-full max-w-md space-y-6 rounded-3xl border border-white/10 bg-white/90 p-8 shadow-2xl backdrop-blur">
          <div className="text-center space-y-2">
            <img
              src={showNeonLogo
                ? `${process.env.PUBLIC_URL}/images/logo/PropShare_Logo_NeonLights.png`
                : `${process.env.PUBLIC_URL}/images/logo/PropShare_Logo_MarketLights.png`
              }
              alt="PropShare"
              className="mx-auto h-14 object-contain"
            />
            <p className="text-sm text-slate-500">
              Enter your email and we will sign you in automatically.
            </p>
          </div>
          <form onSubmit={handleEmailOnlyAuthSubmit} className="space-y-4">
            <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Email address
            </label>
            <input
              type="email"
              autoComplete="email"
              aria-label="Email address"
              value={emailOnlyAuthEmail}
              onChange={(event) => setEmailOnlyAuthEmail(event.target.value)}
              disabled={authLoading}
              className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-base text-slate-900 placeholder:text-slate-400 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200 disabled:cursor-not-allowed disabled:opacity-70"
              placeholder="you@propshare.com"
            />
            {authError && (
              <p className="text-sm text-red-600" role="alert" aria-live="assertive">
                {authError}
              </p>
            )}
            <button
              type="submit"
              disabled={authLoading}
              className="w-full rounded-2xl bg-slate-900 px-4 py-3 text-sm font-semibold uppercase tracking-wide text-white transition hover:bg-black disabled:cursor-not-allowed disabled:opacity-60"
            >
              {authLoading ? 'Signing you in…' : 'Continue with email'}
            </button>
          </form>
          <p className="text-xs text-slate-500">
            This demo adds a hidden password so you only ever type your email.
          </p>
        </div>
      </div>
    );
  }

  if (isInitialLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading PropShare Network...</p>
          <p className="text-sm text-gray-500 mt-2">Connecting to Supabase database...</p>
        </div>
      </div>
    );
  }

  return (
    <SidebarProvider defaultOpen={false}>
      {currentPage === 'onloan' ? (
        <OnLoan onBack={() => setCurrentPage('search')} userProfile={userProfile} />
      ) : currentPage === 'feedback' ? (
        <Feedback
          onBack={() => setCurrentPage('search')}
          userProfile={userProfile}
          onSubmitFeedback={handleFeedbackSubmit}
        />
      ) : currentPage === 'profile' ? (
        <Profile onBack={() => setCurrentPage('search')} userProfile={userProfile} />
      ) : currentPage === 'impact' ? (
        <SeeYourImpact onBack={() => setCurrentPage('search')} />
      ) : currentPage === 'settings' ? (
        <SettingsPage onBack={() => setCurrentPage('search')} userProfile={userProfile} />
      ) : (
      /* Main Search Content Area */
      <main 
        className="flex-1 overflow-hidden relative min-h-screen"
        style={{ 
          '--searchv2-header-offset': '180px',
        }}
      >
      {/* Top Logo Bar */}
      <div className="bg-transparent px-4 pt-4 pb-0 flex justify-center items-center gap-4 relative z-[1]">
        <TooltipTrigger isOpen={showAllTooltips}>
          <AriaButton
            ref={logoButtonRef}
            onPress={() => setIsDropdownOpen(prev => !prev)}
            onHoverStart={() => setIsLogoHovered(true)}
            onHoverEnd={() => setIsLogoHovered(false)}
            className="flex items-center gap-3 px-6 py-3 rounded-full hover:bg-gray-100 transition-all group relative focus:outline-none focus-visible:ring-0"
          >
            <ChevronDown className={`w-4 h-4 text-gray-400 group-hover:text-gray-600 transition-all translate-y-[2px] ${
              isDropdownOpen ? 'rotate-180' : ''
            }`} />
            <img 
              src={showNeonLogo 
                ? `${process.env.PUBLIC_URL}/images/logo/PropShare_Logo_NeonLights.png`
                : `${process.env.PUBLIC_URL}/images/logo/PropShare_Logo_MarketLights.png`
              }
              alt="PropShare" 
              className="h-[4em] transition-opacity duration-300"
            />
            <ChevronDown className={`w-4 h-4 text-gray-400 group-hover:text-gray-600 transition-all translate-y-[2px] ${
              isDropdownOpen ? 'rotate-180' : ''
            }`} />
          </AriaButton>
          <AriaTooltipStyled placement="left">
            {t('searchv2.tooltips.openMenu')}
          </AriaTooltipStyled>
        </TooltipTrigger>
        
      </div>
      
      {/* Dividing Area with Buttons and Fading Line */}
      <div className="relative py-2.5 z-[110]">
        <div className="flex items-center justify-between px-6 relative z-10">
            {/* Left Side - Warehouse Filter */}
          <div className="hidden sm:flex items-center gap-2">
            <TooltipTrigger delay={700} isOpen={showAllTooltips}>
            <DialogTrigger>
            <CircleIconButton aria-label="Select warehouses">
                <LocalStacksIcon className="w-4 h-4" />
              </CircleIconButton>
              <Popover placement="right top">
                <div className="flex flex-col gap-4 w-72">
                  <h3 className="font-semibold text-sm">{t('searchv2.warehouseFilter.title')}</h3>
                  
                  {/* Loading state */}
                  {organizationsLoading ? (
                    <div className="flex items-center justify-center py-4">
                      <div className="text-sm text-gray-500">{t('searchv2.warehouseFilter.loading')}</div>
                    </div>
                  ) : (
                    <>
                      {/* Search Bar */}
                      <div className="relative">
                        <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                          type="text"
                          placeholder={t('searchv2.warehouseFilter.searchPlaceholder')}
                          value={warehouseFilter}
                          onChange={(e) => setWarehouseFilter(e.target.value)}
                          className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                      
                      <div className="flex flex-col gap-2">
                        <div className="flex justify-end mb-1">
                          <label className="flex items-center gap-2 text-xs text-gray-600 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={filteredWarehouses.length > 0 && filteredWarehouses.every(w => selectedStacks.includes(w))}
                              onChange={(e) => handleSelectAll(e.target.checked)}
                              className="rounded focus:outline-none focus-visible:ring-0"
                            />
                            <span>{t('searchv2.warehouseFilter.selectAll')}</span>
                          </label>
                        </div>
                        
                        {filteredWarehouses.length === 0 ? (
                          <p className="text-sm text-gray-500 text-center py-2">{t('searchv2.warehouseFilter.noOrganizations')}</p>
                        ) : (
                          <div className="flex flex-wrap gap-2">
                            {filteredWarehouses.map((warehouse) => (
                              <label
                                key={warehouse}
                                className="flex items-center gap-2 px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-full text-sm cursor-pointer transition-colors"
                              >
                                <input
                                  type="checkbox"
                                  checked={selectedStacks.includes(warehouse)}
                                  onChange={() => toggleStack(warehouse)}
                                  className="rounded focus:outline-none focus-visible:ring-0"
                                />
                                <span>{warehouse}</span>
                              </label>
                            ))}
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>
              </Popover>
            </DialogTrigger>
            <AriaTooltipStyled>
            {t('searchv2.tooltips.selectWarehouses')}
            </AriaTooltipStyled>
            </TooltipTrigger>
          </div>
          
          {/* Fading Line with Page Title */}
          <div className="searchv2-header-divider absolute left-0 right-0 top-0 translate-y-0 flex items-center pointer-events-none z-0 sm:left-0 sm:right-0 sm:top-1/2 sm:-translate-y-1/2">
            <div className="flex-1 h-px bg-gradient-to-r from-transparent via-gray-300 to-gray-300"></div>
            <div className="px-4 pointer-events-auto">
              <div className="gufram-browser gufram-browser--switch">
                <ViewSwitch />
              </div>
            </div>
            <div className="flex-1 h-px bg-gradient-to-l from-transparent via-gray-300 to-gray-300"></div>
          </div>
          
          {/* Right Button (mirrored) with Popover + Tips */}
          <div className="hidden sm:flex items-center gap-2">
            <AriaButton
              aria-label="Toggle labels"
              onPress={() => setShowAllTooltips(!showAllTooltips)}
              className={`group relative z-20 flex items-center h-10 border rounded-full transition-all duration-200 focus:outline-none focus-visible:ring-0 ${
                showAllTooltips
                  ? 'px-3 gap-2 bg-blue-50 text-blue-700 border-blue-200 shadow-sm'
                  : 'w-10 justify-center bg-white border-gray-300 text-gray-500 hover:text-gray-700 hover:border-gray-400 shadow-sm hover:shadow-md hover:w-auto hover:px-3 hover:gap-2'
              }`}
            >
              <Eye className="w-4 h-4 flex-shrink-0 pointer-events-none" />
              <span
                className={`pointer-events-none text-xs whitespace-nowrap overflow-hidden transition-opacity transition-transform duration-200 ${
                  showAllTooltips
                    ? 'max-w-[120px] opacity-100 translate-x-0 ml-1'
                    : 'max-w-0 opacity-0 -translate-x-1 group-hover:max-w-[120px] group-hover:opacity-100 group-hover:translate-x-0 group-hover:ml-1'
                }`}
              >
                {t('searchv2.tooltips.showLabels')}
              </span>
            </AriaButton>

            <LanguageToggle />
            
          </div>
        </div>
      </div>
      
      {/* Dropdown Menu - Drawer Style */}
      {shouldRenderDropdownDrawer && (
        <>
          {/* Backdrop */}
          <div
            className={`fixed inset-0 bg-black/50 transition-opacity duration-300 ${isDropdownAnimating ? 'opacity-100' : 'opacity-0'}`}
            style={{ zIndex: 120 }}
            onClick={() => setIsDropdownOpen(false)}
          />

          {/* Menu Drawer (drops down from the top) */}
          <div
            className={`dropdown-container fixed left-0 right-0 bg-white rounded-b-2xl shadow-2xl transition-transform duration-500 ease-[cubic-bezier(0.32,0.72,0,1)] flex flex-col ${isDropdownAnimating ? 'translate-y-0' : '-translate-y-full'}`}
            style={{
              top: 0,
              width: '100%',
              maxWidth: '340px',
              maxHeight: '85vh',
              margin: '0 auto',
              zIndex: 121,
            }}
          >
            {/* Scrollable Content */}
            <div className="overflow-y-auto px-6 pt-6" style={{paddingBottom:'0.2rem'}}>
            
            {/* Section 1 - Navigation */}
            <div className="mb-4 pb-4 border-b">
              <div className="grid grid-cols-3 gap-2">
                <MenuTileButton 
                  icon={Search} 
                  label={t('searchv2.menu.searchHome')} 
                  onClick={() => {
                    setCurrentPage('search');
                    setIsDropdownOpen(false);
                  }}
                />
                <MenuTileButton 
                  icon={Package} 
                  label={t('searchv2.menu.onLoan')} 
                  onClick={() => {
                    setCurrentPage('onloan');
                    setIsDropdownOpen(false);
                  }}
                />
                <MenuTileButton 
                  icon={FileText} 
                  label="Feedback" 
                  onClick={() => {
                    setCurrentPage('feedback');
                    setIsDropdownOpen(false);
                  }}
                />
              </div>
            </div>

            {/* Section 3 - Impact Mode */}
            <div className="mb-4 pb-4 border-b">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold">{t('searchv2.impactMode.title')}</h3>
                <TooltipTrigger isOpen={showAllTooltips}>
                  <AriaButton
                    className="text-gray-400 hover:text-gray-600 transition-colors focus:outline-none focus-visible:ring-0"
                  >
                    <HelpCircle className="w-4 h-4" />
                  </AriaButton>
                  <AriaTooltipStyled placement="right">
                    {t('searchv2.impactMode.tooltip')}
                  </AriaTooltipStyled>
                </TooltipTrigger>
              </div>
              
              <div className="flex flex-col gap-3">
                <button
                  onClick={() => {
                    console.log('Setting impact mode to environmental, current:', impactMode);
                    setImpactMode(impactMode === 'environmental' ? null : 'environmental');
                  }}
                  className={`group relative p-4 rounded-xl text-sm font-medium transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-green-400 focus:ring-offset-2 ${
                    impactMode === 'environmental'
                      ? 'bg-gradient-to-r from-green-500 to-emerald-600 text-white shadow-xl shadow-green-400/60 scale-105'
                      : 'bg-gray-100 hover:bg-green-50 text-gray-700 hover:shadow-md hover:scale-102'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-2xl">🌱</span>
                    <span className="flex-1 ml-3 text-left">{t('searchv2.impactMode.environment')}</span>
                    {impactMode === 'environmental' && (
                      <span className="text-xs opacity-90">{t('searchv2.impactMode.active')}</span>
                    )}
                  </div>
                </button>
                
                <button
                  onClick={() => setImpactMode(impactMode === 'community' ? null : 'community')}
                  className={`group relative p-4 rounded-xl text-sm font-medium transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-2 ${
                    impactMode === 'community'
                      ? 'bg-gradient-to-r from-blue-500 to-indigo-600 text-white shadow-xl shadow-blue-400/60 scale-105'
                      : 'bg-gray-100 hover:bg-blue-50 text-gray-700 hover:shadow-md hover:scale-102'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-2xl">🤝</span>
                    <span className="flex-1 ml-3 text-left">{t('searchv2.impactMode.community')}</span>
                    {impactMode === 'community' && (
                      <span className="text-xs opacity-90">{t('searchv2.impactMode.active')}</span>
                    )}
                  </div>
                </button>
                
                <button
                  onClick={() => setImpactMode(impactMode === 'cost' ? null : 'cost')}
                  className={`group relative p-4 rounded-xl text-sm font-medium transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:ring-offset-2 ${
                    impactMode === 'cost'
                      ? 'bg-gradient-to-r from-yellow-500 to-amber-600 text-white shadow-xl shadow-yellow-400/60 scale-105'
                      : 'bg-gray-100 hover:bg-yellow-50 text-gray-700 hover:shadow-md hover:scale-102'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-2xl">💰</span>
                    <span className="flex-1 ml-3 text-left">{t('searchv2.impactMode.costSavings')}</span>
                    {impactMode === 'cost' && (
                      <span className="text-xs opacity-90">{t('searchv2.impactMode.active')}</span>
                    )}
                  </div>
                </button>
              </div>
            </div>
            
            {/* Section 4 - Links */}
            <div className="mb-1">
              <div className="flex flex-col gap-1">
                <MenuRowButton 
                  icon={User} 
                  label={t('searchv2.menu.profile')} 
                  onClick={() => {
                    setCurrentPage('profile');
                    setIsDropdownOpen(false);
                  }}
                />
                <button
                  type="button"
                  onClick={() => {
                    handleLogout();
                    setIsDropdownOpen(false);
                  }}
                  className="flex items-center gap-2 text-xs text-gray-400 hover:text-gray-600 transition-colors focus:outline-none focus-visible:ring-0"
                  style={{ paddingLeft: '1.5em', color: 'var(--color-danger-600)' }}
                >
                  <LogOut className="w-3 h-3" />
                  <span>{t('searchv2.menu.signOut')}</span>
                </button>
                <MenuRowButton 
                  icon={TrendingUp} 
                  label={t('searchv2.menu.seeYourImpact')} 
                  onClick={() => {
                    setCurrentPage('impact');
                    setIsDropdownOpen(false);
                  }}
                />
                <MenuRowButton 
                  icon={SettingsIcon} 
                  label={t('searchv2.menu.settings')} 
                  onClick={() => {
                    setCurrentPage('settings');
                    setIsDropdownOpen(false);
                  }}
                /> 
              </div>
              
              {/* De-emphasized warehouse login */}
                <div className="mt-2 pt-2 border-t border-gray-200">
                  <a
                    href={canUseWarehouseLogin ? warehouseLoginHref : undefined}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-disabled={!canUseWarehouseLogin}
                    className={`flex items-center gap-2 text-xs transition-colors focus:outline-none focus-visible:ring-0 ${
                      canUseWarehouseLogin
                        ? 'text-gray-400 hover:text-gray-600'
                        : 'text-gray-300 pointer-events-none'
                    }`}
                  >
                    <LogIn className="w-3 h-3" />
                    <span>{t('searchv2.menu.warehouseLogin')}</span>
                  </a>
                </div>
            </div>
          </div>
          
          {/* Sticky Footer - Manifesto */}
          <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-2 rounded-b-lg">
            <style>{`
              @keyframes shimmer {
                /* Green wave */
                0% {
                  text-shadow: -25px 0 8px rgba(34, 197, 94, 0), -20px 0 6px rgba(34, 197, 94, 0);
                }
                2% {
                  text-shadow: -20px 0 8px rgba(34, 197, 94, 0.1), -18px 0 6px rgba(34, 197, 94, 0.05);
                }
                4% {
                  text-shadow: -16px 0 8px rgba(34, 197, 94, 0.2), -14px 0 6px rgba(34, 197, 94, 0.12);
                }
                6% {
                  text-shadow: -12px 0 8px rgba(34, 197, 94, 0.3), -10px 0 6px rgba(34, 197, 94, 0.2);
                }
                8% {
                  text-shadow: -8px 0 8px rgba(34, 197, 94, 0.4), -6px 0 6px rgba(34, 197, 94, 0.28);
                }
                9% {
                  text-shadow: -4px 0 8px rgba(34, 197, 94, 0.5), -3px 0 6px rgba(34, 197, 94, 0.35);
                }
                10% {
                  text-shadow: 0 0 8px rgba(34, 197, 94, 0.6), 0 0 6px rgba(34, 197, 94, 0.4);
                }
                11% {
                  text-shadow: 4px 0 8px rgba(34, 197, 94, 0.5), 3px 0 6px rgba(34, 197, 94, 0.35);
                }
                12% {
                  text-shadow: 8px 0 8px rgba(34, 197, 94, 0.4), 6px 0 6px rgba(34, 197, 94, 0.28);
                }
                14% {
                  text-shadow: 12px 0 8px rgba(34, 197, 94, 0.3), 10px 0 6px rgba(34, 197, 94, 0.2);
                }
                16% {
                  text-shadow: 16px 0 8px rgba(34, 197, 94, 0.2), 14px 0 6px rgba(34, 197, 94, 0.12);
                }
                18% {
                  text-shadow: 20px 0 8px rgba(34, 197, 94, 0.1), 18px 0 6px rgba(34, 197, 94, 0.05);
                }
                20% {
                  text-shadow: 25px 0 8px rgba(34, 197, 94, 0), 20px 0 6px rgba(34, 197, 94, 0);
                }
                /* Gap */
                20.1%, 33.32% {
                  text-shadow: 25px 0 0px rgba(34, 197, 94, 0), 20px 0 0px rgba(34, 197, 94, 0);
                }
                /* Blue wave */
                33.33% {
                  text-shadow: -25px 0 8px rgba(59, 130, 246, 0), -20px 0 6px rgba(59, 130, 246, 0);
                }
                35.33% {
                  text-shadow: -20px 0 8px rgba(59, 130, 246, 0.1), -18px 0 6px rgba(59, 130, 246, 0.05);
                }
                37.33% {
                  text-shadow: -16px 0 8px rgba(59, 130, 246, 0.2), -14px 0 6px rgba(59, 130, 246, 0.12);
                }
                39.33% {
                  text-shadow: -12px 0 8px rgba(59, 130, 246, 0.3), -10px 0 6px rgba(59, 130, 246, 0.2);
                }
                41.33% {
                  text-shadow: -8px 0 8px rgba(59, 130, 246, 0.4), -6px 0 6px rgba(59, 130, 246, 0.28);
                }
                42.33% {
                  text-shadow: -4px 0 8px rgba(59, 130, 246, 0.5), -3px 0 6px rgba(59, 130, 246, 0.35);
                }
                43.33% {
                  text-shadow: 0 0 8px rgba(59, 130, 246, 0.6), 0 0 6px rgba(59, 130, 246, 0.4);
                }
                44.33% {
                  text-shadow: 4px 0 8px rgba(59, 130, 246, 0.5), 3px 0 6px rgba(59, 130, 246, 0.35);
                }
                45.33% {
                  text-shadow: 8px 0 8px rgba(59, 130, 246, 0.4), 6px 0 6px rgba(59, 130, 246, 0.28);
                }
                47.33% {
                  text-shadow: 12px 0 8px rgba(59, 130, 246, 0.3), 10px 0 6px rgba(59, 130, 246, 0.2);
                }
                49.33% {
                  text-shadow: 16px 0 8px rgba(59, 130, 246, 0.2), 14px 0 6px rgba(59, 130, 246, 0.12);
                }
                51.33% {
                  text-shadow: 20px 0 8px rgba(59, 130, 246, 0.1), 18px 0 6px rgba(59, 130, 246, 0.05);
                }
                53.33% {
                  text-shadow: 25px 0 8px rgba(59, 130, 246, 0), 20px 0 6px rgba(59, 130, 246, 0);
                }
                /* Gap */
                53.34%, 66.65% {
                  text-shadow: 25px 0 0px rgba(59, 130, 246, 0), 20px 0 0px rgba(59, 130, 246, 0);
                }
                /* Gold wave */
                66.66% {
                  text-shadow: -25px 0 8px rgba(234, 179, 8, 0), -20px 0 6px rgba(234, 179, 8, 0);
                }
                68.66% {
                  text-shadow: -20px 0 8px rgba(234, 179, 8, 0.1), -18px 0 6px rgba(234, 179, 8, 0.05);
                }
                70.66% {
                  text-shadow: -16px 0 8px rgba(234, 179, 8, 0.2), -14px 0 6px rgba(234, 179, 8, 0.12);
                }
                72.66% {
                  text-shadow: -12px 0 8px rgba(234, 179, 8, 0.3), -10px 0 6px rgba(234, 179, 8, 0.2);
                }
                74.66% {
                  text-shadow: -8px 0 8px rgba(234, 179, 8, 0.4), -6px 0 6px rgba(234, 179, 8, 0.28);
                }
                75.66% {
                  text-shadow: -4px 0 8px rgba(234, 179, 8, 0.5), -3px 0 6px rgba(234, 179, 8, 0.35);
                }
                76.66% {
                  text-shadow: 0 0 8px rgba(234, 179, 8, 0.6), 0 0 6px rgba(234, 179, 8, 0.4);
                }
                77.66% {
                  text-shadow: 4px 0 8px rgba(234, 179, 8, 0.5), 3px 0 6px rgba(234, 179, 8, 0.35);
                }
                78.66% {
                  text-shadow: 8px 0 8px rgba(234, 179, 8, 0.4), 6px 0 6px rgba(234, 179, 8, 0.28);
                }
                80.66% {
                  text-shadow: 12px 0 8px rgba(234, 179, 8, 0.3), 10px 0 6px rgba(234, 179, 8, 0.2);
                }
                82.66% {
                  text-shadow: 16px 0 8px rgba(234, 179, 8, 0.2), 14px 0 6px rgba(234, 179, 8, 0.12);
                }
                84.66% {
                  text-shadow: 20px 0 8px rgba(234, 179, 8, 0.1), 18px 0 6px rgba(234, 179, 8, 0.05);
                }
                86.66% {
                  text-shadow: 25px 0 8px rgba(234, 179, 8, 0), 20px 0 6px rgba(234, 179, 8, 0);
                }
                /* Gap */
                86.67%, 100% {
                  text-shadow: 25px 0 0px rgba(234, 179, 8, 0), 20px 0 0px rgba(234, 179, 8, 0);
                }
              }
              .manifesto-link span {
                animation: shimmer 9s ease-in-out infinite;
                animation-delay: 2s;
              }
            `}</style>
            <div className="flex items-center justify-center gap-2 text-xs text-gray-500">
              <span>{t('searchv2.menu.readThe')}</span>
              <button 
                onClick={() => {
                  setManifestoModalOpen(true);
                  setIsDropdownOpen(false);
                }}
                className="manifesto-link flex items-center gap-1 hover:text-blue-600 transition-colors focus:outline-none focus-visible:ring-0"
              >
                <FileText className="w-3 h-3" />
                <span>{t('searchv2.menu.manifesto')}</span>
              </button>
            </div>
          </div>

                    {/* Pull Handle */}
          <div 
            className="flex justify-center pb-3 pt-1 cursor-grab active:cursor-grabbing"
            onClick={() => setIsDropdownOpen(false)}
          >
            <div className="w-12 h-1.5 bg-gray-300 rounded-full hover:bg-gray-400 transition-colors" />
          </div>
          
        </div>
      </>
      )}

    
      
      {/* Main Content - Gufram Prop Browser */}
      <div 
        className="fixed top-0 right-0 flex justify-center transition-all duration-200 ease-linear md:bottom-0"
        style={{
          left: 'var(--sidebar-width-icon)',
          bottom: 'var(--sidebar-height-mobile-icon)',
        }}
      >
        {guframPropsLoading ? (
          <div className="flex items-center justify-center w-full h-full">
            <div className="text-gray-500">Loading props...</div>
          </div>
        ) : (
          <GuframPropBrowser
            items={guframProps}
            onAddToBasket={(item) => {
              // Item is already in PropShare format from fetchPropsForGufram
              // Just need to add it to selection
              const propItem = {
                id: item.id,
                name: item.title,
                owner_org_id: item.orgId || organizations[0]?.id,
                organization: item.organization || organizations[0],
                images: item.galleryImages || [],
              };
              
              addToSelection(propItem);
            }}
            onRemoveFromBasket={(item) => {
              removeFromSelection(item.id);
            }}
            isItemInBasket={(itemId) => isSelected(itemId)}
            onPropDetailsRequest={handleOpenPropDetails}
          />
        )}
      </div>
      
      {/* Conveyor Shell - Fixed to bottom */}
      <ConveyorShell>
        <div className="flex flex-col items-end items-stretch w-full gap-0 overflow-visible">
          {/* Impact Summary - always above baskets */}
          <div className="w-auto flex justify-end mb-[-20px] relative pointer-events-none" style={{ zIndex: 70 }}>
            <div className="pointer-events-auto">
            <ImpactSummary 
              selectedItems={selectedItems}
              impactMode={impactMode}
              totalSelectedCount={totalSelectedCount}
              onPromptClick={() => {
                setIsDropdownOpen(true);
              }}
            />
            </div>
          </div>

          {/* Baskets - centered, scrollable with scroll indicators */}
          <div className="flex-1 relative min-w-0 overflow-visible">
            {/* Left gradient fade */}
            <div 
              className="absolute left-0 pointer-events-none"
              style={{
                background: 'linear-gradient(to right, white, transparent)',
                width: '96px',
                top: 0,
                bottom: '-2rem',
                zIndex: 50,
                opacity: showLeftArrow ? 1 : 0,
                transition: 'opacity 0.3s ease-out'
              }}
            />
            
            {/* Right gradient fade */}
            <div 
              className="absolute right-0 pointer-events-none"
              style={{
                background: 'linear-gradient(to left, white, transparent)',
                width: '96px',
                top: 0,
                bottom: '-2rem',
                zIndex: 50,
                opacity: showRightArrow ? 1 : 0,
                transition: 'opacity 0.3s ease-out'
              }}
            />
            
            {/* Left scroll arrow */}
            <button
              onClick={() => scrollBaskets('left')}
              className="absolute left-2 top-1/2 w-10 h-10 bg-white rounded-full shadow-xl flex items-center justify-center text-gray-700 hover:bg-gray-100 hover:scale-110 border-2 border-gray-200"
              style={{ 
                zIndex: 60,
                opacity: showLeftArrow ? 1 : 0,
                pointerEvents: showLeftArrow ? 'auto' : 'none',
                transform: showLeftArrow ? 'translate(0, -50%)' : 'translate(-10px, -50%)',
                transition: 'all 0.3s ease-out'
              }}
              aria-label="Scroll left"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            
            {/* Right scroll arrow */}
            <button
              onClick={() => scrollBaskets('right')}
              className="absolute right-2 top-1/2 w-10 h-10 bg-white rounded-full shadow-xl flex items-center justify-center text-gray-700 hover:bg-gray-100 hover:scale-110 border-2 border-gray-200"
              style={{ 
                zIndex: 60,
                opacity: showRightArrow ? 1 : 0,
                pointerEvents: showRightArrow ? 'auto' : 'none',
                transform: showRightArrow ? 'translate(0, -50%)' : 'translate(10px, -50%)',
                transition: 'all 0.3s ease-out'
              }}
              aria-label="Scroll right"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
              </svg>
            </button>

            <div 
              ref={basketScrollRef}
              onScroll={checkScrollPosition}
              className="flex gap-4 overflow-x-scroll relative z-10 pb-2 flex-nowrap px-[40%] pointer-events-auto"
              style={{ 
                WebkitOverflowScrolling: 'touch',
                scrollbarWidth: 'none',
                msOverflowStyle: 'none',
                maxWidth: '100%',
                width: '100%'
              }}
            >
            {selectedStacks.map((warehouseName, index) => {
              // Get the organization ID from our map
              const orgId = organizationNameToId[warehouseName];
              if (!orgId) {
                console.warn(`No organization ID found for: ${warehouseName}`);
                return null;
              }

              // Use sprite variants cyclically (1-4)
              const spriteVariant = (index % 4) + 1;

              // Get actual item count from Cart V2 state
              const itemCount = getOrgItemCount(orgId);

              return (
                <Basket 
                  key={orgId}
                  orgId={orgId}
                  orgName={warehouseName}
                  itemCount={itemCount}
                  spriteVariant={spriteVariant}
                  onClick={handleBasketClick}
                />
              );
            })}
            </div>
          </div>
        </div>
      </ConveyorShell>

      {/* Prop Details Modal */}
      <PropDetailsModal
        prop={detailProp}
        onClose={handleClosePropDetails}
        onAddToCart={handleAddToCartFromModal}
        userLists={[]}
        allProps={props}
        onPropClick={handleOpenPropDetails}
      />

      {/* Cart Modal */}
      {cartModalData && (
        <CartModal
          isOpen={cartModalOpen}
          onClose={() => {
            setCartModalOpen(false);
            setCartModalOrgId(null);
          }}
          orgId={cartModalData.orgId}
          orgName={cartModalData.orgName}
          items={cartModalData.items}
          itemCount={cartModalData.itemCount}
          basketPosition={cartModalBasketPos}
          onRemoveItem={removeFromSelection}
          onCheckout={handleModalCheckout}
          globalImpactMode={impactMode}
          userProfile={userProfile}
        />
      )}

      {/* Manifesto Modal */}
      <ManifestoModal
        isOpen={manifestoModalOpen}
        onClose={() => setManifestoModalOpen(false)}
      />
      </main>
      )}
    </SidebarProvider>
  );
}

export default SearchV2;
